<script setup lang="ts">
// The Map Review report: one full-screen overlay that states the operational
// questions leading to a reduction in the mosquito population and answers each
// with the panels the server counted for it (doc/design/mvp/report-design.md).
// It replaces the flat dashboard grid — the overlay IS the inventory, grouped
// under the four decision phases: where are we, why is it where it is, is it
// working, what should change.
//
// Three rules shape the whole component:
//
// 1. It renders what the server counted. The client never resolves a clause
//    into a filter; it sends the spec to `POST /api/dashboard` and
//    charts the response, which the server computed with the same resolver the
//    ledger and the map use. A panel therefore cannot disagree with the table
//    beneath it.
// 2. A clicked mark is a filter. Where a bucket's key is a value the ledger's
//    own filter accepts, a click narrows the shared spec and the ledger, the map
//    and the report all follow.
// 3. Not every tally is a drill, and the report says which rather than offering
//    a dead click. A key of "" (an unrecorded method, an unassigned applicator)
//    is drawn muted: no enum filter can select the empty value. A Site- or
//    Trap-Station-keyed bucket is a plain ranked tally, because an identifier is
//    not filterable anywhere in the ledger — no clause would select exactly the
//    rows the bucket counted. And a bucket keyed by a vocabulary the report
//    derives rather than reads (a site-visit outcome, a Feature's production
//    state, a catalog category, a treatment-count band) names no ledger column
//    either, so those panels carry no click.
import { computed, onMounted, ref, watch } from "vue";
import { useDebounceFn } from "@vueuse/core";

import {
	RATE_OVER_COLOR,
	RATE_UNDER_COLOR,
	clientZoneOffsetMinutes,
} from "@/components/map-review/columns";
import { useStoreMapReview } from "@/store/map-review";
import type {
	MapReviewDashboardBucket,
	MapReviewDashboardMeasure,
	MapReviewDashboardPoint,
	MapReviewDashboardUnitTotal,
	MapReviewTable,
} from "@/type/api";
import type { ChartDataset } from "chart.js";

import ExpandedCardLayer from "./cards/ExpandedCardLayer.vue";
import ExpandableCard from "./cards/ExpandableCard.vue";
import BarChart from "./charts/BarChart.vue";
import BaselineChart from "./charts/BaselineChart.vue";
import DoughnutChart from "./charts/DoughnutChart.vue";
import LineChart from "./charts/LineChart.vue";
import StatCard from "./charts/StatCard.vue";
import QuestionPanel from "./report/QuestionPanel.vue";
import ReportPanel from "./report/ReportPanel.vue";
import PopulationHeader from "./population/PopulationHeader.vue";
import PopulationMap from "./population/PopulationMap.vue";
import PopulationSeasons from "./population/PopulationSeasons.vue";
import PopulationSpecies from "./population/PopulationSpecies.vue";
import PopulationTrend from "./population/PopulationTrend.vue";
import PopulationWeekPicker from "./population/PopulationWeekPicker.vue";
import WasteMap from "./waste/WasteMap.vue";
import WasteMethod from "./waste/WasteMethod.vue";
import WasteObservers from "./waste/WasteObservers.vue";
import WasteOutcomeSplit from "./waste/WasteOutcomeSplit.vue";
import WasteRetreatment from "./waste/WasteRetreatment.vue";
import WasteStats from "./waste/WasteStats.vue";
import WasteTopSites from "./waste/WasteTopSites.vue";
import WasteWeeklyBand from "./waste/WasteWeeklyBand.vue";
import {
	MUTED_COLOR,
	OKABE_ITO,
	formatAmount,
	formatCount,
	sliceColor,
	withAlpha,
} from "./charts/theme";

const store = useStoreMapReview();

// The aggregate the panels chart. Null until the first fetch resolves; the
// store refetches it when the overlay opens and after every drill-down.
const data = computed(() => store.dashboard);
// The report is additive: `report` is absent from a payload a server that
// predates it built. The overlay then renders the legacy panels it can still
// chart, says why the question panels are missing, and never fails to draw.
const report = computed(() => data.value?.report ?? null);
const orientation = computed(() => report.value?.orientation);
const diagnosis = computed(() => report.value?.diagnosis);
const effect = computed(() => report.value?.effect);
const allocation = computed(() => report.value?.allocation);
const context = computed(() => report.value?.context);
const waste = computed(() => report.value?.waste);
const areaUnit = computed(() => data.value?.area_unit ?? "");

// The Feature type vocabulary is open-ended (a habitat class per Feature) and so
// is the district's list of technicians: both panels show the head of their
// ranked list and say how long the list is, because a panel of forty bars is a
// panel nobody reads. The ledger's own type and technician filters reach the
// tail.
const FEATURE_TYPE_LIMIT = 12;
const TECHNICIAN_LIMIT = 15;

/** A month series in chart form: the raw "YYYY-MM" keys (a click needs them),
 * the spelled labels, and the counts. */
function monthSeries(points: MapReviewDashboardPoint[] | undefined) {
	const rows = points ?? [];
	return {
		months: rows.map((point) => point.month),
		labels: rows.map((point) => formatMonth(point.month)),
		counts: rows.map((point) => point.count),
	};
}

/** A ranked category list in bar-chart form. `measure` names what the bars
 * count; `limit` trims a long list to the head the panel shows. Bars carry the
 * bucket's area where the server counted one, so the tooltip can pair a count
 * with the area it covered. */
function bars(
	buckets: MapReviewDashboardBucket[] | undefined,
	measure: string,
	limit?: number,
) {
	const rows = (buckets ?? []).slice(0, limit);
	return {
		buckets: rows,
		labels: rows.map((bucket) => bucket.label),
		datasets: [
			{
				label: measure,
				data: rows.map((bucket) => bucket.count),
				// A bucket keyed "" cannot be selected by the ledger's enum
				// filters, so it is drawn muted: the chart shows which marks are
				// interactive without a second legend.
				backgroundColor: rows.map((bucket) =>
					bucket.key === "" ? MUTED_COLOR : OKABE_ITO[0],
				),
				maxBarThickness: 22,
				areas: rows.map((bucket) => bucket.area ?? null),
			},
		],
	};
}

/** A ranked measure list in bar-chart form: the report's own derived figures
 * (a share, a ratio). `scale` converts the measure into the unit the bar reads
 * in — 100 for a share, so the axis and the ticks name percentage points — and
 * the dataset's label states that unit, so a bar is never read as a bare
 * number. Measures are keyed by the same kinds of key as buckets, so none of
 * them is drilled here. */
function measureBars(
	rows: MapReviewDashboardMeasure[] | undefined,
	measure: string,
	scale: number,
	limit?: number,
) {
	const head = (rows ?? []).slice(0, limit);
	return {
		rows: head,
		labels: head.map((row) => row.label),
		datasets: [
			{
				label: measure,
				// Two decimals is the most a ratio this small can carry without
				// printing a tail of digits no operator reads. A key of "" (the
				// unassigned technician) is muted exactly as a bucket's is: no
				// clause selects a null creator, so the bar is not interactive.
				data: head.map((row) => Math.round(row.value * scale * 100) / 100),
				backgroundColor: head.map((row) =>
					row.key === "" ? MUTED_COLOR : OKABE_ITO[0],
				),
				maxBarThickness: 22,
			},
		],
	};
}

/** A closed-vocabulary mix in doughnut form: the parts make up the whole, and
 * every member is present (these vocabularies are small). */
function slices(buckets: MapReviewDashboardBucket[] | undefined) {
	const rows = buckets ?? [];
	return {
		buckets: rows,
		labels: rows.map((bucket) => bucket.label),
		values: rows.map((bucket) => bucket.count),
	};
}

/** One line of a month series. The fill is the line's own hue at low alpha, so
 * two series never need two colour systems. */
function lineDataset(
	label: string,
	values: number[],
	color: string,
): ChartDataset<"line", number[]> {
	return {
		label,
		data: values,
		borderColor: color,
		backgroundColor: withAlpha(color, 0.15),
		borderWidth: 2,
		tension: 0.3,
		pointRadius: 2,
		pointHoverRadius: 4,
		fill: false,
	};
}

/** "2026-06" as "Jun 2026". The tick labels are what make a long series
 * readable, so the raw key stays available for the drill-down. */
const MONTH_NAMES = [
	"Jan",
	"Feb",
	"Mar",
	"Apr",
	"May",
	"Jun",
	"Jul",
	"Aug",
	"Sep",
	"Oct",
	"Nov",
	"Dec",
];

function formatMonth(month: string): string {
	const [year, number] = month.split("-");
	const name = MONTH_NAMES[Number(number) - 1];
	return name ? `${name} ${year}` : month;
}

/** A percentage with one decimal, or an em dash when the server had no
 * denominator to divide by. A figure with no denominator is not a zero, and the
 * card must not print one. */
function percent(value: number | null | undefined): string {
	return value == null ? "—" : `${value.toFixed(1)}%`;
}

/** A row's applied quantity, one term per recorded unit: "42 lb · 3 gal". Empty
 * when nothing was recorded — never "0", because no reading and a zero reading
 * are different facts about different treatments. */
function unitTotals(
	quantities: MapReviewDashboardUnitTotal[] | undefined,
): string {
	return (quantities ?? [])
		.map((total) => `${formatAmount(total.amount)} ${total.unit}`)
		.join(" · ");
}

/** The honest answer to a question the district's schema cannot answer. The
 * server names the gap in `context.unavailable`; the report repeats the finding
 * rather than drawing an empty axis, because a question the operator asked and
 * the data cannot answer is itself a finding. */
function unavailableAnswer(id: string): string {
	const named = context.value?.unavailable ?? [];
	return named.includes(id)
		? "Not recorded in the district's data."
		: "No panel in this report carries this question.";
}

/** Apply a bucket's clause. The key IS the value the ledger's own filter
 * accepts, so the ledger, the map and the full-screen tables narrow to exactly
 * the rows this mark counted. A key of "" has no clause to set — the ledger's
 * enum filters cannot select the empty value — so the click is refused. A
 * measure row carries the same kind of key (a user id, a vocabulary value), so
 * the same clause is emitted for either shape of ranked row. */
function drillBucket(
	table: MapReviewTable,
	column: string,
	rows: { key: string }[],
	index: number,
): void {
	const row = rows[index];
	if (!row || row.key === "") return;
	void store.drillDown({ table, column, op: "enum", values: [row.key] });
}

/** Narrow to one month. The server bucketed the series in the viewer's zone
 * offset, so the clause names the same local days — through the offset AND the
 * day bounds — or the ledger would narrow to a different row set than the bar
 * showed. */
function drillMonth(
	table: MapReviewTable,
	column: string,
	months: string[],
	index: number,
): void {
	const month = months[index];
	if (!month) return;
	const [year, monthNumber] = month.split("-").map(Number);
	if (!year || !monthNumber) return;
	// Day 0 of the following month is the last day of this one, leap years
	// included, which a hand-rolled table of month lengths always gets wrong.
	const lastDay = new Date(year, monthNumber, 0).getDate();
	const pad = (value: number) => String(value).padStart(2, "0");
	void store.drillDown({
		table,
		column,
		op: "dateRange",
		from: `${month}-01`,
		to: `${year}-${pad(monthNumber)}-${pad(lastDay)}`,
		tz_offset_minutes: clientZoneOffsetMinutes(),
	});
}

/** Narrow the Mosquito counts ledger to one species. The catch bucket's key is
 * the species vocabulary id as text (the query's own key), and the ledger's
 * counts clause carries that id as a number, so the key is parsed back rather
 * than matched against a name — a name is neither unique nor stable. */
function drillSpecies(
	buckets: MapReviewDashboardBucket[],
	index: number,
): void {
	const bucket = buckets[index];
	if (!bucket || bucket.key === "") return;
	const species = Number(bucket.key);
	if (!Number.isFinite(species)) return;
	void store.drillDown({
		table: "species_count",
		column: "species",
		op: "enum",
		values: [species],
	});
}

// --- The headline: waste, and the cost proxy -------------------------------
//
// The district's budget is fixed, so the binding concern is reduction per unit
// of effort and the reader must meet the waste first. Waste is the operator's
// definition — any action that does not lead to a reduction — measured at the
// site visit: a visit on which no intervention was applied is waste, even where
// an inspection found production. The reading breakdown is evidence beside the
// measure, not the measure itself.
const wastedShare = computed(() => percent(waste.value?.wasted_percent));
const wasteReadings = computed(() => slices(waste.value?.by_reading));
const wasteSites = computed(() =>
	bars(waste.value?.by_site, "Wasted site-visits"),
);
/** The district's applied quantity per recorded unit, as the treatments were
 * recorded. Units are never summed: "42 lb · 3 gal" is two facts, not 45. */
const appliedQuantity = computed(() =>
	(data.value?.quantity_by_unit ?? [])
		.map((total) => `${formatAmount(total.amount)} ${total.unit}`)
		.join(" · "),
);
/** Site-visits by reading: the observation-level half of the waste measure. */
const readings = computed(() => slices(data.value?.observations_by_reading));
const observationMonths = computed(() =>
	monthSeries(data.value?.observations_by_month),
);
const observationDatasets = computed<ChartDataset<"line", number[]>[]>(() => [
	lineDataset("Observations", observationMonths.value.counts, OKABE_ITO[0]),
]);

// --- A. Where are we -------------------------------------------------------

// A1 — the population over time. Production and the site-visits that looked for
// it are two small multiples on one month axis because their ratio is the
// visit-normalised series the fixed budget makes readable; they are drawn apart
// because the server buckets each series over its own months, and forcing two
// independent series onto one label array would misalign them. The catch series
// stands beside them, spec-scoped, and the trap family's own monthly series
// stands apart because the ledger's filter does not narrow it.
const reportProduction = computed(() =>
	monthSeries(orientation.value?.production_by_month),
);
const reportVisits = computed(() =>
	monthSeries(orientation.value?.visits_by_month),
);
const productionDatasets = computed<ChartDataset<"line", number[]>[]>(() => [
	lineDataset("Production found", reportProduction.value.counts, OKABE_ITO[0]),
]);
const visitDatasets = computed<ChartDataset<"line", number[]>[]>(() => [
	lineDataset("Site-visits", reportVisits.value.counts, OKABE_ITO[1]),
]);
const catchMonths = computed(() => monthSeries(data.value?.catches_by_month));
const catchDatasets = computed<ChartDataset<"line", number[]>[]>(() => [
	lineDataset("Mosquitoes caught", catchMonths.value.counts, OKABE_ITO[0]),
]);
const abundanceMonths = computed(() =>
	monthSeries(orientation.value?.abundance_by_month),
);
const abundanceDatasets = computed<ChartDataset<"line", number[]>[]>(() => [
	lineDataset("Analysed catch", abundanceMonths.value.counts, OKABE_ITO[2]),
]);

// A2 — the reference. The surveillance section reads the district's traps, not
// the ledger's filter spec, so its series carries no drill-down, exactly as the
// season-keyed series always has.
const surveillance = computed(() => store.surveillance);
const weekLabel = computed(
	() => surveillance.value?.week_label ?? "Disease week (MMWR)",
);
const baselineYears = computed(() => surveillance.value?.baseline_years ?? []);
const totalPerTrap = computed(() => surveillance.value?.total_per_trap ?? []);
const speciesPerTrap = computed(
	() => surveillance.value?.species_per_trap ?? [],
);
/** True when the trap family has a value-bearing week to draw. Before the trap
 * import runs every week is a gap, so the section says so rather than drawing
 * empty axes. */
const hasSurveillance = computed(
	() =>
		totalPerTrap.value.some((week) => week.value != null) ||
		speciesPerTrap.value.some((species) =>
			species.weeks.some((week) => week.value != null),
		),
);

// A3 — where it is worst. Every one of these is keyed by a place (a Site, a Trap
// Station), which the ledger cannot filter on, so they are ranked tallies: the
// measure and its denominator are in the panel sub and the bars carry no click.
const productionSites = computed(() =>
	bars(orientation.value?.production_by_site, "Productive site-visits"),
);
const catchSites = computed(() =>
	bars(orientation.value?.catches_by_site, "Mosquitoes caught"),
);
const catchStations = computed(() =>
	bars(orientation.value?.catches_by_station, "Mosquitoes caught"),
);
const activeFeatures = computed(() => slices(data.value?.features_by_active));

// A4 — what dominates. The species key IS the ledger's counts clause (the
// species id), so that ring drills; the stage and density vocabularies are
// display-only on the observation row, so those rings do not.
const catchSpecies = computed(() => slices(data.value?.catches_by_species));
const inspectionStages = computed(() =>
	slices(orientation.value?.inspections_by_stage),
);
const inspectionDensities = computed(() =>
	slices(orientation.value?.inspections_by_density),
);
const treatmentCategories = computed(() =>
	slices(allocation.value?.treatments_by_category),
);

// --- B. Why is it where it is ----------------------------------------------

// B5 — what is producing. Habitat is the ledger's Feature type, so those bars
// narrow the ledger; the ownership ring is keyed by the Site's owner kind, which
// the ledger does filter on.
const productionHabitats = computed(() =>
	bars(
		diagnosis.value?.production_by_habitat,
		"Productive observations",
		FEATURE_TYPE_LIMIT,
	),
);
const sourceHabitats = computed(() =>
	bars(
		diagnosis.value?.source_features_by_habitat,
		"Source Features",
		FEATURE_TYPE_LIMIT,
	),
);
const featureTypes = computed(() =>
	bars(data.value?.features_by_type, "Features", FEATURE_TYPE_LIMIT),
);
const siteOwners = computed(() => slices(data.value?.sites_by_owner_kind));

// B6 — the unknown. A Feature's production state is asserted from its
// inspections and observations, never stored, so no ledger filter selects it and
// the ring is a tally. The Site kinds are the mapped estate's own vocabulary.
const productionStates = computed(() =>
	slices(diagnosis.value?.features_by_production_state),
);
const siteKinds = computed(() => slices(data.value?.sites_by_kind));

// B7 — the source population under control. The control state IS the ledger's
// derived verdict, so this ring drills (and is the same expression the map bands
// by).
const controlStates = computed(() =>
	slices(data.value?.features_by_control_state),
);

// B8 — the adulticide, and whether it holds. The median is measured FROM the
// residual expiry, so it answers "how long after the suppression lapsed did
// production come back"; a negative value is a return before the window lapsed.
const rebound = computed(() => diagnosis.value?.rebound);
const medianReturn = computed(() =>
	rebound.value?.median_days_to_return == null
		? "—"
		: `${formatAmount(rebound.value.median_days_to_return)} days`,
);

// B10 — the repeat applications per product per Site. Keyed by the place, so
// it is a tally.
const productUse = computed(() =>
	bars(diagnosis.value?.product_use_by_site, "Treatments"),
);

// E23 — public complaints. They are the organisation's reports, not the
// ledger's rows, so neither the month series nor the type ring drills.
const complaintMonths = computed(() =>
	monthSeries(context.value?.complaints_by_month),
);
const complaintDatasets = computed<ChartDataset<"line", number[]>[]>(() => [
	lineDataset("Complaints", complaintMonths.value.counts, OKABE_ITO[0]),
]);
const complaintTypes = computed(() =>
	slices(context.value?.complaints_by_type),
);

// --- C. Is it working ------------------------------------------------------

// C11 — usage against outcome. The alignment puts treatments and catches on one
// month axis as two aligned series; a dual axis would invite a false reading of
// where they cross, and the month belongs to two tables, so neither series
// drills.
const alignment = computed(() => data.value?.usage_vs_catches ?? []);
const alignmentLabels = computed(() =>
	alignment.value.map((pair) => formatMonth(pair.month)),
);
const alignmentDatasets = computed<ChartDataset<"line", number[]>[]>(() => [
	lineDataset(
		"Treatments",
		alignment.value.map((pair) => pair.usage),
		OKABE_ITO[0],
	),
	lineDataset(
		"Mosquitoes caught",
		alignment.value.map((pair) => pair.catches),
		OKABE_ITO[1],
	),
]);
const treatmentOutcomes = computed(() =>
	slices(effect.value?.treatment_outcomes),
);

// C12 — re-treatment without effect. The two bars are read as a pair: the
// Features in each treatment-count band, and the production those same bands
// carry. Neither vocabulary is a ledger column, so neither drills.
const retreatmentCounts = computed(() =>
	bars(diagnosis.value?.retreatments_by_feature, "Features"),
);
const productionByTreatments = computed(() =>
	bars(
		diagnosis.value?.production_by_treatment_count,
		"Productive observations",
	),
);

// C13 — the thing we just killed, found again. The trap positivity ring is
// keyed by the ledger's own positivity vocabulary, so it drills; the
// post-treatment figures are the rebound fold's.
const positivity = computed(() => slices(data.value?.trap_positivity));

// C14 — which areas meet the target. The shares are read as percentages of the
// site-visits that looked and of the observations a technician recorded.
const siteShares = computed(() =>
	measureBars(
		effect.value?.production_share_by_site,
		"Share of site-visits that found production (%)",
		100,
	),
);
const applicatorShares = computed(() =>
	measureBars(
		effect.value?.production_share_by_applicator,
		"Share of observations that found production (%)",
		100,
	),
);

// --- D. What should change -------------------------------------------------

// D15 — the cost proxy. One table per product and per Site, because quantity
// per unit and area and minutes are three axes that no single bar can carry.
const spendProducts = computed(() => allocation.value?.spend_by_product ?? []);
const spendSites = computed(() => allocation.value?.spend_by_site ?? []);
const products = computed(() =>
	bars(data.value?.treatments_by_product, "Treatments"),
);
const applicators = computed(() =>
	bars(data.value?.treatments_by_applicator, "Treatments"),
);
const fieldMinuteMonths = computed(() =>
	monthSeries(allocation.value?.field_minutes_by_month),
);
const fieldMinuteDatasets = computed<ChartDataset<"line", number[]>[]>(() => [
	lineDataset(
		"Recorded field minutes",
		fieldMinuteMonths.value.counts,
		OKABE_ITO[0],
	),
]);

// D16 — the marginal return, as finds per hundred site-visits (the same ratio
// the server computed, in the unit a bar axis can tick).
const marginalSites = computed(() =>
	measureBars(
		allocation.value?.marginal_by_site,
		"Production found per 100 site-visits",
		100,
	),
);

// D17 — the effort beside the productivity, per Site, and the effort each
// technician recorded.
const effortSites = computed(() => allocation.value?.effort_by_site ?? []);
const technicians = computed(() =>
	bars(
		data.value?.observations_by_technician,
		"Observations",
		TECHNICIAN_LIMIT,
	),
);

// D18 — the mix. Usage over time (count against area, two aligned small
// multiples), the method vocabulary, the catalog category, and the applied-rate
// verdicts — every one of them a ledger vocabulary except the category, which is
// a catalog dimension with no ledger filter.
const usage = computed(() => monthSeries(data.value?.treatments_by_month));
const usageCountDatasets = computed<ChartDataset<"line", number[]>[]>(() => [
	lineDataset("Treatments", usage.value.counts, OKABE_ITO[0]),
]);
/** True when any month carries area at all. A missing area sum is zero for that
 * month, but a series of nothing but zeroes would read as "no area applied"
 * when the truth is that no area was recorded — so the chart is omitted and the
 * panel says so instead. */
const hasUsageArea = computed(() =>
	(data.value?.treatments_by_month ?? []).some(
		(point) => (point.area ?? 0) > 0,
	),
);
const usageAreaDatasets = computed<ChartDataset<"line", number[]>[]>(() => [
	lineDataset(
		`Area treated (${areaUnit.value})`,
		(data.value?.treatments_by_month ?? []).map(
			(point) => Math.round((point.area ?? 0) * 10) / 10,
		),
		OKABE_ITO[1],
	),
]);
const methods = computed(() => slices(data.value?.treatments_by_method));
const verdicts = computed(() => slices(data.value?.rate_verdicts));
// The verdict colours reuse the ledger's own over/under pair (columns.ts), so a
// treatment that reads vermillion in the table reads vermillion in the ring. No
// colour is load-bearing on its own: every slice is named in the legend.
const VERDICT_COLORS: Record<string, string> = {
	over: RATE_OVER_COLOR,
	under: RATE_UNDER_COLOR,
	in_range: OKABE_ITO[2],
	unknown: OKABE_ITO[3],
	not_applicable: "#6c757d",
};
const verdictColors = computed(() =>
	verdicts.value.buckets.map(
		(bucket, index) => VERDICT_COLORS[bucket.key] ?? sliceColor(index),
	),
);
const inRangeLabel = computed(() => percent(data.value?.in_range_percent));

// D19 — the next allocation, by the rule the report states.
const underServiced = computed(
	() => allocation.value?.under_serviced_sites ?? [],
);

// D20 — the ceiling: production at Features nothing is currently suppressing.
// The control state is the ledger's own derived verdict, so those bars drill.
const outsideControl = computed(() =>
	bars(diagnosis.value?.production_outside_control, "Productive observations"),
);

/** The head's scope line: what the whole report is narrowed to. The panels are
 * computed over this spec, so it must be legible from inside the overlay — a
 * report whose numbers are a mystery is worse than no report. */
const scope = computed(() => {
	const parts: string[] = [];
	if (store.spec.filters.length > 0) {
		const clauses = store.spec.filters.length;
		parts.push(`${clauses} filter ${clauses === 1 ? "clause" : "clauses"}`);
	}
	if (store.spec.zones.length > 0) {
		parts.push(
			store.spec.zones.length === 1
				? "a zone"
				: `${store.spec.zones.length} zones`,
		);
	}
	if (store.spec.temp_zones.length > 0) {
		parts.push(
			store.spec.temp_zones.length === 1
				? "a temporary zone"
				: `${store.spec.temp_zones.length} temporary zones`,
		);
	}
	const features = store.spec.feature_ids?.length ?? 0;
	if (features > 0) {
		parts.push(
			features === 1 ? "one Feature" : `${features.toLocaleString()} Features`,
		);
	}
	const searched = Object.values(store.spec.search).some(
		(text) => text.trim() !== "",
	);
	if (searched) parts.push("a search");
	return parts.length === 0
		? "Whole district — no filters"
		: `Scoped to ${parts.join(", ")}`;
});

/** The applied-filter indicator's tooltip: one line per clause the spec is
 * narrowing by, in the operator's own words. It names every clause the ledger
 * can set — including the ones this question does not read — so the chip is a
 * complete account rather than a count of the panel's own favourites. */
const appliedFilterTitle = computed(() =>
	store.appliedFilters
		.map((clause) =>
			clause.detail ? `${clause.label} — ${clause.detail}` : clause.label,
		)
		.join("\n"),
);

/** The clauses the waste question deliberately ignores, grouped into the
 * three dimensions a visit-level reading has no use for. */
const ignoredLabel = computed(() => {
	const groups = new Set<string>();
	for (const clause of store.waste?.ignored ?? []) {
		if (clause.table === "species_count") groups.add("species and sex");
		else if (clause.table === "zone") groups.add("zone");
		else groups.add("trap");
	}
	const named = [...groups];
	if (named.length === 0) return "";
	const last = named.pop() as string;
	return named.length === 0 ? last : `${named.join(", ")} and ${last}`;
});

// ── The report's inventory ───────────────────────────────────────────────
// The questions the report answers, in the order it asks them, under the
// phases it groups them by. The chooser is built from this list and each
// question's panels take their own heading back out of it, so the wording
// lives once. A question's `help` is the line the report states under it —
// what its panels measure, and the denominators they divide by.
interface ReportEntry {
	/** The id the question's panels are keyed by. */
	id: string;
	/** Its number in the inventory, where the inventory gives it one. */
	number?: string;
	/** The question itself: the panel's heading once the question is open. */
	label: string;
	/** The chooser's button text, where the question's own sentence is too long
	 * for a rail. The panel heading still states the question in full. */
	choice?: string;
	/** The line under it, and the button's tooltip. */
	help: string;
}

interface ReportPhase {
	/** A stable key for the group. */
	key: string;
	/** The phase's letter, or "" for the questions above the four phases. */
	letter: string;
	/** The phase's own name. */
	title: string;
	/** What the phase is for. */
	lede: string;
	entries: ReportEntry[];
}

const REPORT_PHASES: ReportPhase[] = [
	{
		key: "waste",
		letter: "",
		title: "Waste",
		lede: "",
		entries: [
			{
				id: "headline",
				label: "Waste — effort that did not reduce the population",
				help: "A site visit is one Site on one client-local day carrying at least one observation. The binding measure is the share of those visits that applied no intervention at all: a visit that found production and treated nothing is waste, exactly as a visit that found nothing is. Cost is the district's own units: the quantity as recorded, the extent treated, and recorded minutes.",
			},
			{
				id: "W1",
				label: "How much of the field effort applied no intervention?",
				help: "Wasted site visits ÷ all site visits. Waste is the operator's definition — any action that does not lead to a reduction — so a visit that applied nothing is waste even where its inspection found production; treated and wasted visits account for every site visit. Denominator: site visits (one Site on one client-local day carrying at least one observation).",
			},
			{
				id: "W2",
				label: "What did the visits find, and when did we visit?",
				help: "Observations by reading (production found ÷ found none ÷ no inspection) and observation volume by local month. Denominator: observations under the current filter.",
			},
		],
	},
	{
		key: "A",
		letter: "A",
		title: "Where are we",
		lede: "What the population is doing, against what reference, where it is worst, and which species and stages dominate — the frame every later question is read in.",
		entries: [
			{
				id: "A1",
				number: "A1",
				label: "What is the population doing over time?",
				help: "Production found and the site-visits that looked for it, per client-local month — the visit-normalised pair. Denominator: site-visits (one Site on one local day with an observation). Beside it the spec-scoped catch, and the trap family's own per-month series, which the ledger's filter does not narrow.",
			},
			{
				id: "A2",
				number: "A2",
				label: "Against what reference?",
				help: "This report year's catch per trap against the prior five seasons' minimum-to-maximum band and their mean, by disease week. The band's denominator is the analysed trap samples of those seasons; a week with no samples is a gap, never a zero.",
			},
			{
				id: "E21",
				number: "E21",
				label: "Does weather or season drive the production?",
				help: "Degree-days and rainfall against production. The driver data would have to come from a weather series at the district's own location.",
			},
			{
				id: "E24",
				number: "E24",
				label: "How does the district compare with its neighbours?",
				help: "Neighbouring districts, shared catchments and their disease signals — the picture outside this district's own account.",
			},
			{
				id: "A3",
				number: "A3",
				label: "Where is it worst?",
				help: "Productive site-visits per Site, caught mosquitoes per Site and per Trap Station, and how much of the mapped estate is still in service. Denominator: site-visits for production, analysed trap samples for catch.",
			},
			{
				id: "A4",
				number: "A4",
				label: "What species and stages dominate?",
				help: "Caught mosquitoes by species (denominator: the kept traps' species counts), the ladder stage and larval density the live inspections recorded (denominator: live inspections at kept Features), and the larvicide/adulticide split. The species small multiples under A2 name which species carry the season.",
			},
		],
	},
	{
		key: "B",
		letter: "B",
		title: "Why is it where it is",
		lede: "What is producing the mosquitoes, how much of that we know about, what we are doing about it, and whether it is coming from outside.",
		entries: [
			{
				id: "B5",
				number: "B5",
				label: "What is producing the mosquitoes?",
				help: "Productive observations and confirmed sources by habitat — ranked by productivity, not by how often the place was visited (denominator: kept Features). The registered habitat inventory, and who owns the places that produce.",
			},
			{
				id: "B6",
				number: "B6",
				label: "How much production is unknown?",
				help: "Kept Features by the production state their inspections and observations assert, and the share that is undetermined — no state determined plus a state of unknown, over all kept Features. That gap is habitat the operation cannot be efficient over. Beside it, the mapped estate by site kind.",
			},
			{
				id: "B7",
				number: "B7",
				label: "What born-in-place production are we killing?",
				help: "Of the kept Features in production state source, how many are under current control: controlled ÷ source (the coverage of the places known to produce). And every kept Feature's control state.",
			},
			{
				id: "B8",
				number: "B8",
				label: "What are we doing to the adults, and does it hold?",
				help: "Kept treatments by catalog category — the adulticide share of the chemical effort — and what followed each treatment: returned, suppressed, unconfirmed, and the median days to return over the treatments that returned.",
			},
			{
				id: "B9",
				number: "B9",
				label: "Is the population coming from outside?",
				help: "Caught mosquitoes by Trap Station and by Site. A station catching heavily while its own Site's production is low points outside the Site; the baseline band under A2 is the seasonal reference.",
			},
			{
				id: "B10",
				number: "B10",
				label: "Are the chemicals still working?",
				help: "Repeat applications per product per Site — the top 15 pairs by treatment count. A pair that recurs is a place being re-treated with that product; the re-treatment counts read beside their production are under C12.",
			},
			{
				id: "E22",
				number: "E22",
				label: "Is land use or development creating new production?",
				help: "Land use, development and new construction as sources of production. The district's classification carries habitat, water origin, size, organic content, flow, connectivity and vegetation influence.",
			},
			{
				id: "E23",
				number: "E23",
				label: "What are the public complaints pointing at?",
				help: "Public complaints filed with the district, by client-local month and by report type. Complaints are the organisation's own reports, so the ledger's filter does not narrow them.",
			},
		],
	},
	{
		key: "C",
		letter: "C",
		title: "Is it working",
		lede: "Whether the treatment suppressed production and for how long, whether effort is landing where the production is, and whether what we find afterwards is what we killed.",
		entries: [
			{
				id: "C11",
				number: "C11",
				label: "Did treatment suppress production there, and for how long?",
				help: "Features that carry at least one treatment, by the outcome of their latest one judged by the next observation at that Feature (denominator: treated Features), with the median days from residual expiry to a return — measured from when the suppression lapses, so a negative value means production returned before the window lapsed. Beside it, treatments applied against mosquitoes caught, on one shared month axis.",
			},
			{
				id: "C12",
				number: "C12",
				label: "Are we re-treating the same places without effect?",
				help: "Kept Features that carry at least one kept treatment, by how many they carry, and the productive observations in each band. Read beside each other, the pair shows whether more treatments go with less production or with the same production. Returns inside the residual window were {{ formatCount(rebound?.within_window_returns ?? 0) }} — the waste case.",
			},
			{
				id: "C13",
				number: "C13",
				label: "Are we finding the thing we just killed where we killed it?",
				help: "Post-treatment positivity — post-treatment observations whose reading is found, over all post-treatment observations — and the trap family's own positivity. Neither is a vector index: a true one needs the sample pool tables, which are not imported.",
			},
			{
				id: "C14",
				number: "C14",
				label: "Which areas meet the target and which do not?",
				help: "The share of site-visits that found production, per Site (productive site-visits ÷ site-visits, so one area compares with another without the confound of effort) and per the technician who recorded the observations (found ÷ observations).",
			},
		],
	},
	{
		key: "D",
		letter: "D",
		title: "What should change",
		lede: "Under a fixed budget: what the operation costs in the units the district records, where the next unit of effort buys most, and where the ceiling is.",
		entries: [
			{
				id: "D15",
				number: "D15",
				label: "What does the operation cost, in what units?",
				help: "Quantity per recorded unit — never summed across units, because a litre and a pound are not commensurable — the extent treated in the district's own area unit, and the recorded field minutes, per product and per Site. The district-wide totals are in the headline above; minutes are a lower bound, since only inspections and treatments carry a window.",
			},
			{
				id: "D16",
				number: "D16",
				label: "Where is the marginal return highest?",
				help: "Production found per site-visit, per Site, as finds per hundred site-visits (top 15 by site-visits). This is where one more visit has historically been most likely to find production. Site-keyed, so ranked and not drillable.",
			},
			{
				id: "D17",
				number: "D17",
				label: "Are we over-servicing some places and under-servicing others?",
				help: "Per Site (top 15 by site-visits): the visits made, the ones that found production, the ones that applied no intervention, the treatments, the recorded minutes and applied quantity, and the productive features and production found beside them. Also the field observations each technician recorded.",
			},
			{
				id: "D18",
				number: "D18",
				label:
					"What is the mix — larvae against adults, source reduction against chemical?",
				help: "Treatments applied per client-local month with the area they covered, the method (chemical, biological, physical), the catalog category (larvicide against adulticide), and the applied-rate verdict — the share applied within label, over the treatments that carried a rate basis.",
			},
			{
				id: "D19",
				number: "D19",
				label: "Given the budget, what is the best next allocation?",
				help: "The top 15 Sites with production found and no Feature under current control, ranked by production found. This states the rule the report ranks by; it is not an optimum, which would need a response model the data does not support.",
			},
			{
				id: "D20",
				number: "D20",
				label: "Where is the ceiling?",
				help: "Productive observations by their Feature's control state. Production at a past-control or control-unknown Feature is population nothing is currently suppressing — immigration, cryptic habitat, resistance or simply untreated. Denominator: productive observations.",
			},
		],
	},
];

// ── The population view ──────────────────────────────────────────────────
// The bivariate H3 map and its chart panels are the report's newest question,
// so they are not in REPORT_PHASES: the inventory above stays exactly as it
// was, and restoring its questions is a one-line change to the flag below.
// While the population view is being built out, the chooser offers this
// question and nothing else.
const POPULATION_QUESTION_ONLY = true;

/** The population question, stated as its panels answer it. */
const POPULATION_PHASE: ReportPhase = {
	key: "population",
	letter: "",
	/* The button names the question; the panel states it. A phase heading above
	   the button would repeat the word, so there is none. */
	title: "",
	lede: "The trap field at H3 resolution 8: how many mosquitoes per trap each cell carries, and which way it is moving.",
	entries: [
		{
			id: "population",
			choice: "Population",
			label: "Where is the population, and where is it going?",
			help: "Every H3 cell is filled by smoothed catch per trap at the selected disease week against its trailing six-week slope; the season and the week are chosen above the map, and the whole view — field, breaks, readings and chart axis — is read at that week. A filter overrides the choice and reads the filtered set's own dates instead. Clicking a class narrows the spec to that class's cells. Catch per trap = Σ species counts ÷ analysed samples; the slope is OLS of ln(level) over the trailing six weeks and needs four weeks with data.",
		},
	],
};

/** The waste question, stated as its panels answer it. */
const WASTE_PHASE: ReportPhase = {
	key: "waste",
	letter: "",
	title: "",
	lede: "Site-visits, what each one produced, and where the effort went without a reduction.",
	entries: [
		{
			id: "waste",
			choice: "Waste",
			label: "Are we wasting effort?",
			help: "A site-visit is one Site on one client-local day; it is treated when a kept treatment was applied to any Feature of that Site that day. The five outcomes partition every visit, the weekly chart sets this season's no-treatment share against the five seasons before it, the re-treatment buckets name the Features a click selects, and the H3 map fills each cell by visit volume against the share of those visits that treated. Filters are the ledger's own — the whole spec travels.",
		},
	],
};

/** Every question the source inventory can answer, population and waste first. */
const ALL_PHASES: ReportPhase[] = [
	POPULATION_PHASE,
	WASTE_PHASE,
	...REPORT_PHASES,
];

/** What the chooser offers: the two built-out questions while the workbench is
 * showing them; the whole inventory otherwise. */
const chooserPhases = computed<ReportPhase[]>(() =>
	POPULATION_QUESTION_ONLY ? [POPULATION_PHASE, WASTE_PHASE] : ALL_PHASES,
);

/** The question whose panels are on screen, or null while the operator is still
 * choosing. Opening the pane shows the chooser and nothing else: no question is
 * read, so nothing is counted before it is asked for. */
const selectedQuestion = ref<string | null>(null);

/** A question's QuestionPanel props, read out of the inventory. */
function ask(id: string) {
	const entry = ALL_PHASES.flatMap((phase) => phase.entries).find(
		(candidate) => candidate.id === id,
	);
	if (!entry) return { question: id, sub: "" };
	return entry.number === undefined
		? { question: entry.label, sub: entry.help }
		: { number: entry.number, question: entry.label, sub: entry.help };
}

// A built-out question is read once when the pane opens, once when the
// operator switches to it, and again after every spec change — the same rule
// the rest of the workbench follows. The refetch is debounced so a burst of
// filter edits asks once, and a second call while the same read is in flight is
// the store's to dedupe. Only the question on screen is read: the population
// and waste payloads each carry a map's worth of geometry.
function loadSelectedQuestion(): void {
	if (selectedQuestion.value === "waste") void store.loadWaste();
	else if (selectedQuestion.value === "population") void store.loadPopulation();
}

const reloadQuestion = useDebounceFn(() => {
	loadSelectedQuestion();
}, 300);

onMounted(() => {
	// The pane can be closed while a filter changes elsewhere on the
	// workbench, so opening it re-asks even when a payload is cached: the
	// store's one-body-one-request key makes an unchanged spec a no-op and a
	// changed one a fresh read.
	loadSelectedQuestion();
});

watch(selectedQuestion, () => loadSelectedQuestion());

watch(
	() => store.spec,
	() => reloadQuestion(),
	{ deep: true },
);
</script>

<template>
	<div class="analysis-pane">
		<!-- The head: what the whole report is narrowed to, and — whenever the
		     spec narrows anything at all — a chip whose tooltip lists every
		     applied clause. The chip is not the waste panel's own count: it
		     reads the shared spec, so a clause this question ignores is still
		     named rather than hidden. -->
		<div class="analysis-head">
			<p class="analysis-scope">{{ scope }}</p>
			<span
				v-if="store.specNarrowed"
				class="analysis-filter-chip"
				:title="appliedFilterTitle"
				:aria-label="`${store.appliedFilters.length} applied filter clauses`"
			>
				<i class="bi bi-funnel"></i>
				{{ store.appliedFilters.length }}
			</span>
		</div>

		<!-- The chooser: every question the report answers, in the order it asks
		     them, grouped by the phase it belongs to. A button's tooltip is the
		     line the report states under the question, so the denominators are
		     named without opening anything. -->
		<nav class="analysis-chooser" aria-label="Report questions">
			<template v-for="phase in chooserPhases" :key="phase.key">
				<div v-if="phase.title" class="analysis-phase" :title="phase.lede">
					{{ phase.title }}
				</div>
				<button
					v-for="entry in phase.entries"
					:key="entry.id"
					type="button"
					class="analysis-choice"
					:class="{ active: entry.id === selectedQuestion }"
					:title="entry.help"
					:aria-pressed="entry.id === selectedQuestion"
					@click="selectedQuestion = entry.id"
				>
					<span v-if="entry.number" class="analysis-choice-id">{{
						entry.number
					}}</span
					>{{ entry.choice ?? entry.label }}
				</button>
			</template>
		</nav>

		<!-- The built-out questions: each reads its own payload, so neither waits
		     on nor is hidden by the dashboard's. Only the question on screen is
		     read. -->
		<template v-if="POPULATION_QUESTION_ONLY">
			<div v-if="selectedQuestion === 'waste'" class="analysis-body">
				<QuestionPanel v-bind="ask('waste')">
					<div
						v-if="store.wasteLoading && !store.waste"
						class="analysis-state population-state"
					>
						<div class="spinner-border text-primary" role="status"></div>
						<p class="mt-2 mb-0 text-muted">Counting the season…</p>
					</div>
					<p v-else-if="!store.waste" class="report-notice">
						{{ store.error || "No waste view for the current filter." }}
					</p>
					<template v-else>
						<p v-if="ignoredLabel" class="report-notice waste-ignored">
							<i class="bi bi-info-circle me-1"></i>
							A visit-level waste reading has no {{ ignoredLabel }} dimension,
							so those clauses are ignored here: the panels count every visit in
							the current selection.
						</p>
						<WasteStats :view="store.waste" />
						<WasteOutcomeSplit :view="store.waste" />
						<WasteWeeklyBand :view="store.waste" />
						<WasteMap
							:view="store.waste"
							:selected-class="store.wasteClass"
							:on-map="store.wasteOnMap"
							@select-class="store.toggleWasteClass"
							@send-to-map="store.sendWasteToMap"
						/>
						<WasteTopSites :view="store.waste" />
						<WasteRetreatment :view="store.waste" />
						<WasteObservers :view="store.waste" />
						<WasteMethod :view="store.waste" />
					</template>
				</QuestionPanel>
			</div>

			<div v-else-if="selectedQuestion === 'population'" class="analysis-body">
				<QuestionPanel v-bind="ask('population')">
					<div
						v-if="store.populationLoading && !store.population"
						class="analysis-state population-state"
					>
						<div class="spinner-border text-primary" role="status"></div>
						<p class="mt-2 mb-0 text-muted">Building the population view…</p>
					</div>
					<p v-else-if="!store.population" class="report-notice">
						{{ store.error || "No population view for the current filter." }}
					</p>
					<template v-else>
						<!-- The week control sits above the map because it moves everything
						     beneath it: the crop is what the field, the breaks, the header
						     readings and the chart axis are all read at. -->
						<PopulationWeekPicker
							:year="store.population.year"
							:last-week="store.population.lastWeek"
							:season-first="store.population.seasonFirst"
							:season-last="store.population.seasonLast"
							:seasons="store.population.seasons"
							:selection="store.populationWeek"
							:filtered="store.population.filtered"
							:loading="store.populationLoading"
							:range="store.population.range"
							@select="store.setPopulationWeek"
						/>
						<PopulationMap
							:view="store.population"
							:selected-class="store.populationClass"
							:on-map="store.populationOnMap"
							@select-class="store.togglePopulationClass"
							@send-to-map="store.sendPopulationToMap"
						/>
						<!-- Each reading can be asked for at full size: the rail is
						     narrow, and a season curve or a species band is hard to
						     read at a third of the screen. The wrapper keeps the card
						     a live instance and only moves it into the overlay. -->
						<ExpandableCard label="the season at a glance">
							<PopulationHeader
								:stats="store.population.surveillance.stats"
								:last-week="store.population.lastWeek"
							/>
						</ExpandableCard>
						<ExpandableCard label="catch per trap, by disease week">
							<PopulationSeasons
								:surveillance="store.population.surveillance"
							/>
						</ExpandableCard>
						<ExpandableCard label="the trajectory">
							<PopulationTrend :surveillance="store.population.surveillance" />
						</ExpandableCard>
						<ExpandableCard label="the species breakdown">
							<PopulationSpecies
								:surveillance="store.population.surveillance"
							/>
						</ExpandableCard>
					</template>
				</QuestionPanel>
			</div>
		</template>

		<template v-else>
			<div v-if="store.dashboardLoading && !data" class="analysis-state">
				<div class="spinner-border text-primary" role="status"></div>
				<p class="mt-2 mb-0 text-muted">Building the report…</p>
			</div>
			<div v-else-if="!data" class="analysis-state">
				<p class="mb-0 text-muted">
					{{ store.error || "No report data for the current filter." }}
				</p>
			</div>

			<!-- A drill-down refetches the whole report; until it lands the panels keep
			     their place but take no clicks, because a second click against stale
			     numbers would narrow to the wrong set. -->
			<div
				v-else-if="selectedQuestion"
				class="analysis-body"
				:class="{ 'is-loading': store.dashboardLoading }"
			>
				<p v-if="!report" class="report-notice">
					This payload carries no report: the server that built it predates the
					question-led report. The ledger panels below still render; the
					question panels appear once the server is current.
				</p>

				<!-- The headline. Waste is the operator's definition — any action
				     that does not lead to a reduction — measured at the site visit,
				     so it is the first thing read. -->
				<ReportPanel
					v-if="report"
					title="Waste — effort that did not reduce the population"
				>
					<template #help>
						<p>
							A site visit is one Site on one client-local day carrying at least
							one observation. The binding measure is the share of those visits
							that applied no intervention at all: a visit that found production
							and treated nothing is waste, exactly as a visit that found
							nothing is. Cost is the district's own units: the quantity as
							recorded, the extent treated, and recorded minutes.
						</p>
					</template>
					<div class="kpi-row">
						<StatCard
							label="Wasted-visit share"
							:value="wastedShare"
							sub="site visits that applied no intervention ÷ all site visits"
						/>
						<StatCard
							label="Wasted site-visits"
							:value="formatCount(waste?.wasted_visits ?? 0)"
							sub="no intervention was applied on the visit"
						/>
						<StatCard
							label="Site-visits"
							:value="formatCount(waste?.site_visits ?? 0)"
							sub="one Site on one local day with an observation"
						/>
						<StatCard
							label="Treated site-visits"
							:value="formatCount(waste?.treated_visits ?? 0)"
							sub="an intervention was applied on the visit"
						/>
						<StatCard
							v-if="data.area_treated != null"
							label="Area treated"
							:value="formatAmount(data.area_treated)"
							:sub="data.area_unit"
						/>
						<StatCard
							label="Field minutes"
							:value="formatCount(allocation?.field_minutes ?? 0)"
							sub="recorded visit time — a lower bound"
						/>
					</div>
					<!-- A district records many quantity units (eight in this clone's
					     own data), and a card clips its value to one line — which would
					     hide the very totals the cost proxy exists to carry. The list
					     is a wrapping line instead: one term per recorded unit, never a
					     sum across units. -->
					<p class="headline-quantity">
						Applied quantity, per recorded unit — never summed:
						{{ appliedQuantity || "none recorded" }}
					</p>
				</ReportPanel>

				<QuestionPanel v-if="selectedQuestion === 'W1'" v-bind="ask('W1')">
					<ReportPanel title="What the visits turned up">
						<template #help>
							<p>
								The site visits banded by their observation check — production
								found, found none, or no inspection at all. This is information
								beside the waste measure, not the waste measure: the denominator
								is all site visits, and a visit that found production and
								applied nothing still reads here as production found.
							</p>
							<p>
								Reading the two panels together is the point: a high
								production-found share with a high wasted-visit share is effort
								that keeps making contact without applying anything. A reading
								is not a ledger filter value, so this ring is not clickable.
							</p>
						</template>
						<p v-if="wasteReadings.buckets.length === 0" class="panel-empty">
							No observations under the current filter, so there are no site
							visits to classify.
						</p>
						<div v-else class="panel-chart">
							<DoughnutChart
								:labels="wasteReadings.labels"
								:values="wasteReadings.values"
							/>
						</div>
					</ReportPanel>

					<ReportPanel title="Wasted site-visits by site">
						<template #help>
							<p>
								Top 15 Sites by wasted site visits — the places where visits
								apply nothing. An identifier is not filterable in the ledger, so
								this is a ranked tally rather than a drill.
							</p>
						</template>
						<p v-if="wasteSites.buckets.length === 0" class="panel-empty">
							No wasted site visits under the current filter.
						</p>
						<div v-else class="panel-chart">
							<BarChart
								horizontal
								:labels="wasteSites.labels"
								:datasets="wasteSites.datasets"
							/>
						</div>
					</ReportPanel>
				</QuestionPanel>

				<QuestionPanel v-if="selectedQuestion === 'W2'" v-bind="ask('W2')">
					<ReportPanel title="Observations by reading">
						<p v-if="readings.buckets.length === 0" class="panel-empty">
							No observations under the current filter.
						</p>
						<div v-else class="panel-chart">
							<DoughnutChart
								:labels="readings.labels"
								:values="readings.values"
								@select="
									(i: number) =>
										drillBucket(
											'observation',
											'inspection',
											readings.buckets,
											i,
										)
								"
							/>
						</div>
					</ReportPanel>

					<ReportPanel title="Observation volume by month">
						<template #help>
							<p>
								One point per client-local month. Click a month to narrow the
								ledger to it.
							</p>
						</template>
						<p v-if="observationMonths.months.length === 0" class="panel-empty">
							No observations under the current filter.
						</p>
						<div v-else class="panel-chart">
							<LineChart
								:labels="observationMonths.labels"
								:datasets="observationDatasets"
								@select="
									(i: number) =>
										drillMonth(
											'observation',
											'observed_at',
											observationMonths.months,
											i,
										)
								"
							/>
						</div>
					</ReportPanel>
				</QuestionPanel>

				<QuestionPanel v-if="selectedQuestion === 'A1'" v-bind="ask('A1')">
					<ReportPanel
						v-if="report"
						title="Production found against the visits that looked"
					>
						<template #help>
							<p>
								Two aligned small multiples on one month axis: production
								divided by site-visits is the visit-normalised series, which
								needs no effort to read. Click a month in either chart to narrow
								the ledger to it.
							</p>
							<p>
								A site-visit is one Site on one client-local day carrying at
								least one observation: the denominator of the production series.
							</p>
						</template>
						<div class="split">
							<div class="split-row">
								<p class="chart-caption">Production found per month</p>
								<p
									v-if="reportProduction.months.length === 0"
									class="panel-empty"
								>
									No production was found under the current filter.
								</p>
								<div v-else class="panel-chart">
									<LineChart
										:labels="reportProduction.labels"
										:datasets="productionDatasets"
										@select="
											(i: number) =>
												drillMonth(
													'observation',
													'observed_at',
													reportProduction.months,
													i,
												)
										"
									/>
								</div>
							</div>
							<div class="split-row">
								<p class="chart-caption">Site-visits per month</p>
								<p v-if="reportVisits.months.length === 0" class="panel-empty">
									No site-visits under the current filter.
								</p>
								<div v-else class="panel-chart">
									<LineChart
										:labels="reportVisits.labels"
										:datasets="visitDatasets"
										@select="
											(i: number) =>
												drillMonth(
													'observation',
													'observed_at',
													reportVisits.months,
													i,
												)
										"
									/>
								</div>
							</div>
						</div>
					</ReportPanel>

					<ReportPanel title="Catches by month">
						<template #help>
							<p>
								Caught mosquitoes (species counts) per local month, over the
								kept traps. Click a month to narrow the counts ledger.
							</p>
						</template>
						<p v-if="catchMonths.months.length === 0" class="panel-empty">
							No analysed catches for the kept traps.
						</p>
						<div v-else class="panel-chart">
							<LineChart
								:labels="catchMonths.labels"
								:datasets="catchDatasets"
								@select="
									(i: number) =>
										drillMonth('species_count', 'set_at', catchMonths.months, i)
								"
							/>
						</div>
					</ReportPanel>

					<ReportPanel v-if="report" title="Analysed catch by month">
						<template #help>
							<p>
								The surveillance family's analysed catch, summed by set-day
								month — the season's own catch, at a month's resolution. It is
								not narrowed by the ledger's filter and carries no drill-down,
								exactly as the season-keyed surveillance series does.
							</p>
						</template>
						<p v-if="abundanceMonths.months.length === 0" class="panel-empty">
							The trap family's monthly series is empty: no analysed samples in
							this clone.
						</p>
						<div v-else class="panel-chart">
							<LineChart
								:labels="abundanceMonths.labels"
								:datasets="abundanceDatasets"
							/>
						</div>
					</ReportPanel>
				</QuestionPanel>

				<QuestionPanel v-if="selectedQuestion === 'A2'" v-bind="ask('A2')">
					<ReportPanel title="Mosquitoes per trap, by disease week">
						<p v-if="!hasSurveillance" class="panel-empty">
							No trap surveillance data yet.
						</p>
						<div v-else class="panel-chart">
							<BaselineChart
								:weeks="totalPerTrap"
								label="Total"
								:baseline-years="baselineYears"
								:week-label="weekLabel"
								:ariaLabel="`Mosquitoes per trap by disease week: this season against the prior five seasons' range and mean.`"
							/>
						</div>
					</ReportPanel>

					<ReportPanel
						v-if="speciesPerTrap.length > 0"
						title="Females per trap by species"
					>
						<template #help>
							<p>
								One line per species, each against its own five-season band —
								the small multiples that name which species dominate this
								season.
							</p>
						</template>
						<div class="species-grid">
							<div
								v-for="speciesRow in speciesPerTrap"
								:key="speciesRow.species_id"
								class="species-cell"
							>
								<p class="chart-caption">{{ speciesRow.name }}</p>
								<div class="panel-chart">
									<BaselineChart
										:weeks="speciesRow.weeks"
										:label="speciesRow.name"
										:baseline-years="baselineYears"
										:week-label="weekLabel"
										:ariaLabel="`Females per trap of ${speciesRow.name} by disease week: this season against the prior five seasons' range and mean.`"
									/>
								</div>
							</div>
						</div>
					</ReportPanel>
				</QuestionPanel>

				<!-- E21 and E24 are the reference frame's missing halves. The
				     district asked the question; the schema cannot answer it, and
				     the report says so rather than drawing an empty axis. -->
				<QuestionPanel v-if="selectedQuestion === 'E21'" v-bind="ask('E21')">
					<ReportPanel title="Weather and degree-days">
						<template #help>
							<p>
								No weather, temperature, rainfall or degree-day table exists in
								the district's schema. The season is carried by the surveillance
								report year above; the driver data behind it is absent.
							</p>
						</template>
						<p class="panel-empty">{{ unavailableAnswer("weather") }}</p>
					</ReportPanel>
				</QuestionPanel>

				<QuestionPanel v-if="selectedQuestion === 'E24'" v-bind="ask('E24')">
					<ReportPanel title="The regional picture">
						<template #help>
							<p>
								Reads are scoped to this organisation, so a neighbouring
								district's data is not reachable from this clone. The nearest
								instruments are the baseline band above and the VectorSurv
								transfer surface.
							</p>
						</template>
						<p class="panel-empty">{{ unavailableAnswer("regional") }}</p>
					</ReportPanel>
				</QuestionPanel>

				<QuestionPanel v-if="selectedQuestion === 'A3'" v-bind="ask('A3')">
					<ReportPanel v-if="report" title="Productive site-visits by site">
						<template #help>
							<p>
								Top 15 Sites by productive site-visits. A Site is not filterable
								by its identifier in the ledger, so this is a ranked tally, not
								a drill.
							</p>
						</template>
						<p v-if="productionSites.buckets.length === 0" class="panel-empty">
							No productive site-visits under the current filter.
						</p>
						<div v-else class="panel-chart">
							<BarChart
								horizontal
								:labels="productionSites.labels"
								:datasets="productionSites.datasets"
							/>
						</div>
					</ReportPanel>

					<ReportPanel title="Catches by site">
						<template #help>
							<p>
								Top 15 Sites by caught mosquitoes. Ranked tally, not a drill:
								the ledger filters no identifier.
							</p>
						</template>
						<p v-if="catchSites.buckets.length === 0" class="panel-empty">
							No analysed catches under the current filter.
						</p>
						<div v-else class="panel-chart">
							<BarChart
								horizontal
								:labels="catchSites.labels"
								:datasets="catchSites.datasets"
							/>
						</div>
					</ReportPanel>

					<ReportPanel title="Catches by trap station">
						<template #help>
							<p>
								Top 15 Trap Stations by caught mosquitoes. A station catching
								heavily while its own Site produces little points outside the
								Site (B9). Ranked tally, not a drill.
							</p>
						</template>
						<p v-if="catchStations.buckets.length === 0" class="panel-empty">
							No analysed catches under the current filter.
						</p>
						<div v-else class="panel-chart">
							<BarChart
								horizontal
								:labels="catchStations.labels"
								:datasets="catchStations.datasets"
							/>
						</div>
					</ReportPanel>

					<ReportPanel title="The mapped estate in service">
						<template #help>
							<p>
								Kept Features by the district's standing decision to keep
								servicing them. Denominator: Features under the current filter.
							</p>
						</template>
						<p v-if="activeFeatures.buckets.length === 0" class="panel-empty">
							No Features under the current filter.
						</p>
						<div v-else class="panel-chart">
							<DoughnutChart
								:labels="activeFeatures.labels"
								:values="activeFeatures.values"
								@select="
									(i: number) =>
										drillBucket('feature', 'active', activeFeatures.buckets, i)
								"
							/>
						</div>
					</ReportPanel>
				</QuestionPanel>

				<QuestionPanel v-if="selectedQuestion === 'A4'" v-bind="ask('A4')">
					<ReportPanel title="Catches by species">
						<template #help>
							<p>
								Species dominance under the current filter. Click a slice to
								narrow the counts ledger to that species; an unknown-species key
								has no clause and is refused.
							</p>
						</template>
						<p v-if="catchSpecies.buckets.length === 0" class="panel-empty">
							No analysed catches under the current filter.
						</p>
						<div v-else class="panel-chart">
							<DoughnutChart
								:labels="catchSpecies.labels"
								:values="catchSpecies.values"
								@select="(i: number) => drillSpecies(catchSpecies.buckets, i)"
							/>
						</div>
					</ReportPanel>

					<ReportPanel v-if="report" title="Inspections by ladder stage">
						<template #help>
							<p>
								Live inspections at kept Features, by the stage the ladder had
								reached. Denominator: live inspections.
							</p>
							<p>
								The stage is display-only on the observation row: the ledger
								narrows by the production reading, not by the stage, so this
								ring is not clickable.
							</p>
						</template>
						<p v-if="inspectionStages.buckets.length === 0" class="panel-empty">
							No live inspections under the current filter.
						</p>
						<div v-else class="panel-chart">
							<DoughnutChart
								:labels="inspectionStages.labels"
								:values="inspectionStages.values"
							/>
						</div>
					</ReportPanel>

					<ReportPanel v-if="report" title="Inspections by larval density">
						<template #help>
							<p>
								Live inspections at kept Features, by the density the technician
								recorded. Denominator: live inspections.
							</p>
							<p>
								Empty where the visit ran no check, and display-only on the
								observation row, so this ring is not clickable either.
							</p>
						</template>
						<p
							v-if="inspectionDensities.buckets.length === 0"
							class="panel-empty"
						>
							No live inspections under the current filter.
						</p>
						<div v-else class="panel-chart">
							<DoughnutChart
								:labels="inspectionDensities.labels"
								:values="inspectionDensities.values"
							/>
						</div>
					</ReportPanel>

					<ReportPanel v-if="report" title="Larvicide against adulticide">
						<template #help>
							<p>
								Kept treatments by the applied product's catalog category.
								Denominator: kept treatments.
							</p>
							<p>
								The catalog category is a product dimension, not a ledger
								column, so there is no clause that would select these rows: the
								ring is not clickable.
							</p>
						</template>
						<p
							v-if="treatmentCategories.buckets.length === 0"
							class="panel-empty"
						>
							No kept treatments under the current filter.
						</p>
						<div v-else class="panel-chart">
							<DoughnutChart
								:labels="treatmentCategories.labels"
								:values="treatmentCategories.values"
							/>
						</div>
					</ReportPanel>
				</QuestionPanel>

				<QuestionPanel v-if="selectedQuestion === 'B5'" v-bind="ask('B5')">
					<ReportPanel v-if="report" title="Production by habitat">
						<template #help>
							<p>
								Observations that found production, by their Feature's type.
								{{ productionHabitats.buckets.length }} of
								{{ diagnosis?.production_by_habitat?.length ?? 0 }} habitats
								shown, ranked by productivity. Click a bar to narrow the
								Features ledger to that habitat.
							</p>
						</template>
						<p
							v-if="productionHabitats.buckets.length === 0"
							class="panel-empty"
						>
							No production was found under the current filter.
						</p>
						<div v-else class="panel-chart">
							<BarChart
								horizontal
								:labels="productionHabitats.labels"
								:datasets="productionHabitats.datasets"
								@select="
									(i: number) =>
										drillBucket(
											'feature',
											'type',
											productionHabitats.buckets,
											i,
										)
								"
							/>
						</div>
					</ReportPanel>

					<ReportPanel v-if="report" title="Confirmed sources by habitat">
						<template #help>
							<p>
								Kept Features in production state source, by habitat — the
								standing list of born-in-place production.
								{{ sourceHabitats.buckets.length }} of
								{{ diagnosis?.source_features_by_habitat?.length ?? 0 }}
								habitats shown.
							</p>
						</template>
						<p v-if="sourceHabitats.buckets.length === 0" class="panel-empty">
							No Features are in production state source under the current
							filter.
						</p>
						<div v-else class="panel-chart">
							<BarChart
								horizontal
								:labels="sourceHabitats.labels"
								:datasets="sourceHabitats.datasets"
								@select="
									(i: number) =>
										drillBucket('feature', 'type', sourceHabitats.buckets, i)
								"
							/>
						</div>
					</ReportPanel>

					<ReportPanel title="Features by type">
						<template #help>
							<p>
								{{ featureTypes.buckets.length }} of
								{{ data.features_by_type?.length ?? 0 }} types shown. Click a
								bar to narrow the Features ledger.
							</p>
						</template>
						<p v-if="featureTypes.buckets.length === 0" class="panel-empty">
							No Features under the current filter.
						</p>
						<div v-else class="panel-chart">
							<BarChart
								horizontal
								:labels="featureTypes.labels"
								:datasets="featureTypes.datasets"
								@select="
									(i: number) =>
										drillBucket('feature', 'type', featureTypes.buckets, i)
								"
							/>
						</div>
					</ReportPanel>

					<ReportPanel title="Who owns the places">
						<template #help>
							<p>
								Kept Sites by owner kind — private ground against public.
								Denominator: Sites under the current filter.
							</p>
						</template>
						<p v-if="siteOwners.buckets.length === 0" class="panel-empty">
							No Sites under the current filter.
						</p>
						<div v-else class="panel-chart">
							<DoughnutChart
								:labels="siteOwners.labels"
								:values="siteOwners.values"
								@select="
									(i: number) =>
										drillBucket('site', 'owner_kind', siteOwners.buckets, i)
								"
							/>
						</div>
					</ReportPanel>
				</QuestionPanel>

				<QuestionPanel v-if="selectedQuestion === 'B6'" v-bind="ask('B6')">
					<ReportPanel v-if="report" title="Features by production state">
						<template #help>
							<p>
								The ontology's production state, derived per Feature, never
								stored. Denominator: kept Features.
							</p>
							<p>
								The production state is derived, so no ledger column carries it
								and the ring is not clickable.
							</p>
						</template>
						<p v-if="productionStates.buckets.length === 0" class="panel-empty">
							No Features under the current filter.
						</p>
						<div v-else class="panel-chart">
							<DoughnutChart
								:labels="productionStates.labels"
								:values="productionStates.values"
							/>
						</div>
					</ReportPanel>

					<ReportPanel v-if="report" title="Production still unknown">
						<template #help>
							<p>
								Registered habitat whose production nobody has established. The
								operation cannot aim effort at what it cannot see.
							</p>
						</template>
						<div class="panel-kpi">
							<StatCard
								label="Undetermined"
								:value="percent(diagnosis?.unknown_production_percent)"
								sub="of kept Features: no state determined, or state unknown"
							/>
						</div>
					</ReportPanel>

					<ReportPanel title="Sites by kind">
						<template #help>
							<p>
								The mapped estate's own vocabulary. Denominator: Sites under the
								current filter.
							</p>
						</template>
						<p v-if="siteKinds.buckets.length === 0" class="panel-empty">
							No Sites under the current filter.
						</p>
						<div v-else class="panel-chart">
							<DoughnutChart
								:labels="siteKinds.labels"
								:values="siteKinds.values"
								@select="
									(i: number) =>
										drillBucket('site', 'kind', siteKinds.buckets, i)
								"
							/>
						</div>
					</ReportPanel>
				</QuestionPanel>

				<QuestionPanel v-if="selectedQuestion === 'B7'" v-bind="ask('B7')">
					<ReportPanel
						v-if="report"
						title="Control coverage of the source population"
					>
						<div class="kpi-row">
							<StatCard
								label="Confirmed sources"
								:value="formatCount(diagnosis?.source_features_total ?? 0)"
								sub="kept Features in state source"
							/>
							<StatCard
								label="Under current control"
								:value="formatCount(diagnosis?.source_features_controlled ?? 0)"
								sub="of those, with a live residual window"
							/>
							<StatCard
								label="Source coverage"
								:value="percent(diagnosis?.source_control_percent)"
								sub="controlled ÷ source Features"
							/>
						</div>
					</ReportPanel>

					<ReportPanel title="Feature control state">
						<template #help>
							<p>
								What is under current control, what is past its window, and what
								cannot be judged. Click a slice to narrow the Features ledger.
							</p>
						</template>
						<p v-if="controlStates.buckets.length === 0" class="panel-empty">
							No Features under the current filter.
						</p>
						<div v-else class="panel-chart">
							<DoughnutChart
								:labels="controlStates.labels"
								:values="controlStates.values"
								@select="
									(i: number) =>
										drillBucket(
											'feature',
											'control_state',
											controlStates.buckets,
											i,
										)
								"
							/>
						</div>
					</ReportPanel>
				</QuestionPanel>

				<QuestionPanel v-if="selectedQuestion === 'B8'" v-bind="ask('B8')">
					<ReportPanel v-if="report" title="Adulticide against larvicide">
						<template #help>
							<p>
								The same category split A4 draws, read here for what is aimed at
								the adults. Denominator: kept treatments.
							</p>
						</template>
						<p
							v-if="treatmentCategories.buckets.length === 0"
							class="panel-empty"
						>
							No kept treatments under the current filter.
						</p>
						<div v-else class="panel-chart">
							<DoughnutChart
								:labels="treatmentCategories.labels"
								:values="treatmentCategories.values"
							/>
						</div>
					</ReportPanel>

					<ReportPanel v-if="report" title="What followed the treatment">
						<template #help>
							<p>
								Each kept treatment paired with the first observation after it
								at the same Feature. Denominator: kept treatments.
							</p>
							<p>
								A treatment with no later observation is unconfirmed, never read
								as success: the report never takes silence for evidence.
							</p>
						</template>
						<div class="kpi-row">
							<StatCard
								label="Treated"
								:value="formatCount(rebound?.treated ?? 0)"
								sub="kept treatments"
							/>
							<StatCard
								label="Checked after treatment"
								:value="formatCount(rebound?.observed ?? 0)"
								sub="of the treated, with a later observation to read"
							/>
							<StatCard
								label="Returned"
								:value="formatCount(rebound?.returned ?? 0)"
								sub="the next check found production again"
							/>
							<StatCard
								label="Suppressed"
								:value="formatCount(rebound?.suppressed ?? 0)"
								sub="the next check ran and found none"
							/>
							<StatCard
								label="Unconfirmed"
								:value="formatCount(rebound?.unconfirmed ?? 0)"
								sub="no check, or no later observation at all"
							/>
							<StatCard
								label="Median days from expiry to return"
								:value="medianReturn"
								sub="over the treatments that returned, measured from residual expiry; a negative value returned before the window lapsed"
							/>
						</div>
					</ReportPanel>
				</QuestionPanel>

				<QuestionPanel v-if="selectedQuestion === 'B9'" v-bind="ask('B9')">
					<ReportPanel title="Catches by trap station">
						<template #help>
							<p>
								Top 15 Trap Stations by caught mosquitoes. Ranked tally, not a
								drill: the ledger filters no identifier.
							</p>
						</template>
						<p v-if="catchStations.buckets.length === 0" class="panel-empty">
							No analysed catches under the current filter.
						</p>
						<div v-else class="panel-chart">
							<BarChart
								horizontal
								:labels="catchStations.labels"
								:datasets="catchStations.datasets"
							/>
						</div>
					</ReportPanel>

					<ReportPanel title="Catches by site">
						<template #help>
							<p>
								Top 15 Sites by caught mosquitoes, against the production each
								Site's own Features show. Ranked tally, not a drill.
							</p>
						</template>
						<p v-if="catchSites.buckets.length === 0" class="panel-empty">
							No analysed catches under the current filter.
						</p>
						<div v-else class="panel-chart">
							<BarChart
								horizontal
								:labels="catchSites.labels"
								:datasets="catchSites.datasets"
							/>
						</div>
					</ReportPanel>
				</QuestionPanel>

				<QuestionPanel v-if="selectedQuestion === 'B10'" v-bind="ask('B10')">
					<ReportPanel v-if="report" title="Product use by site">
						<template #help>
							<p>
								Each bar is one Site-and-product pair, labelled together. Ranked
								tally, not a drill: the pair is not a ledger clause, and a Site
								is not filterable by its identifier.
							</p>
						</template>
						<p v-if="productUse.buckets.length === 0" class="panel-empty">
							No kept treatments under the current filter.
						</p>
						<div v-else class="panel-chart">
							<BarChart
								horizontal
								:labels="productUse.labels"
								:datasets="productUse.datasets"
							/>
						</div>
					</ReportPanel>
				</QuestionPanel>

				<!-- E22 and E23 inform the diagnosis: land use is named as missing,
				     and the public's complaints are the one outside signal the
				     district does record. -->
				<QuestionPanel v-if="selectedQuestion === 'E22'" v-bind="ask('E22')">
					<ReportPanel title="Land use and development">
						<template #help>
							<p>
								There is no land-use dimension in the district's classification,
								so new production from development cannot be attributed from
								this data.
							</p>
						</template>
						<p class="panel-empty">{{ unavailableAnswer("land_use") }}</p>
					</ReportPanel>
				</QuestionPanel>

				<QuestionPanel v-if="selectedQuestion === 'E23'" v-bind="ask('E23')">
					<ReportPanel title="Complaints by month">
						<p v-if="complaintMonths.months.length === 0" class="panel-empty">
							No public complaints recorded for this organisation.
						</p>
						<div v-else class="panel-chart">
							<LineChart
								:labels="complaintMonths.labels"
								:datasets="complaintDatasets"
							/>
						</div>
					</ReportPanel>

					<ReportPanel title="Complaints by type">
						<template #help>
							<p>Nuisance against water, with the status left uncollapsed.</p>
						</template>
						<p v-if="complaintTypes.buckets.length === 0" class="panel-empty">
							No public complaints recorded for this organisation.
						</p>
						<div v-else class="panel-chart">
							<DoughnutChart
								:labels="complaintTypes.labels"
								:values="complaintTypes.values"
							/>
						</div>
					</ReportPanel>
				</QuestionPanel>

				<QuestionPanel v-if="selectedQuestion === 'C11'" v-bind="ask('C11')">
					<ReportPanel title="Treatments applied against mosquitoes caught">
						<template #help>
							<p>
								Two aligned series on one month axis, never a second axis: where
								the lines cross is the comparison the panel exists to make. Both
								series span two tables, so neither is a drill.
							</p>
						</template>
						<p v-if="alignment.length === 0" class="panel-empty">
							No kept treatments and no catches to align.
						</p>
						<div v-else class="panel-chart">
							<LineChart
								:labels="alignmentLabels"
								:datasets="alignmentDatasets"
							/>
						</div>
					</ReportPanel>

					<ReportPanel v-if="report" title="Feature treatment outcomes">
						<template #help>
							<p>
								Each Feature's latest treatment, judged by the next observation
								there. Denominator: Features carrying a treatment.
							</p>
							<p>
								Median days from residual expiry to a return:
								{{ medianReturn }} (measured from expiry, so a negative value
								means the return preceded the lapse) —
								{{ formatCount(rebound?.unconfirmed ?? 0) }} treatments remain
								unconfirmed. An outcome is not a ledger filter value, so this
								ring is not clickable.
							</p>
						</template>
						<p
							v-if="treatmentOutcomes.buckets.length === 0"
							class="panel-empty"
						>
							No treated Features under the current filter.
						</p>
						<div v-else class="panel-chart">
							<DoughnutChart
								:labels="treatmentOutcomes.labels"
								:values="treatmentOutcomes.values"
							/>
						</div>
					</ReportPanel>
				</QuestionPanel>

				<QuestionPanel v-if="selectedQuestion === 'C12'" v-bind="ask('C12')">
					<ReportPanel v-if="report" title="Features by treatment count">
						<template #help>
							<p>
								Denominator: kept Features carrying at least one kept treatment.
							</p>
							<p>
								A treatment-count band is not a ledger column, so this chart is
								not clickable.
							</p>
						</template>
						<p
							v-if="retreatmentCounts.buckets.length === 0"
							class="panel-empty"
						>
							No Features under the current filter.
						</p>
						<div v-else class="panel-chart">
							<BarChart
								:labels="retreatmentCounts.labels"
								:datasets="retreatmentCounts.datasets"
							/>
						</div>
					</ReportPanel>

					<ReportPanel v-if="report" title="Production by treatment count">
						<template #help>
							<p>
								Productive observations in the same bands; the C12 evidence read
								beside the chart at left.
							</p>
						</template>
						<p
							v-if="productionByTreatments.buckets.length === 0"
							class="panel-empty"
						>
							No production found under the current filter.
						</p>
						<div v-else class="panel-chart">
							<BarChart
								:labels="productionByTreatments.labels"
								:datasets="productionByTreatments.datasets"
							/>
						</div>
					</ReportPanel>

					<ReportPanel v-if="report" title="Returns inside the residual window">
						<template #help>
							<p>
								The two are the same event read against the product's own
								promise: a return inside the window is the suppression failing,
								and a return after it is the window ending.
							</p>
						</template>
						<div class="kpi-row">
							<StatCard
								label="Within-window returns"
								:value="formatCount(rebound?.within_window_returns ?? 0)"
								sub="÷ all treated: the suppression did not hold for the window the product promises"
								:accent="RATE_OVER_COLOR"
							/>
							<StatCard
								label="After-window returns"
								:value="formatCount(rebound?.after_window_returns ?? 0)"
								sub="÷ all treated: the product's window expiring — expected, and not waste"
							/>
						</div>
					</ReportPanel>
				</QuestionPanel>

				<QuestionPanel v-if="selectedQuestion === 'C13'" v-bind="ask('C13')">
					<ReportPanel v-if="report" title="Post-treatment positivity">
						<template #help>
							<p>
								The next check at a treated Feature, after the treatment. This
								is the closest instrument the current data supports to a vector
								index, and the report never calls it one.
							</p>
						</template>
						<div class="kpi-row">
							<StatCard
								label="Post-treatment positivity"
								:value="percent(rebound?.post_treatment_positive_percent)"
								sub="positive ÷ post-treatment observations"
							/>
							<StatCard
								label="Post-treatment checks"
								:value="formatCount(rebound?.post_treatment_observations ?? 0)"
								sub="observations following a treatment at the same Feature"
							/>
							<StatCard
								label="Found production"
								:value="formatCount(rebound?.post_treatment_positive ?? 0)"
								sub="of those checks"
							/>
						</div>
					</ReportPanel>

					<ReportPanel title="Trap positivity">
						<template #help>
							<p>
								The kept trap samples' own positivity. Click a slice to narrow
								the counts ledger.
							</p>
							<p>
								A vector index would need the pooled-sample tests, which the
								surveillance lane has not imported: this is trap positivity, not
								a vector index.
							</p>
						</template>
						<p v-if="positivity.buckets.length === 0" class="panel-empty">
							No trap samples in this clone, so positivity has nothing to read.
						</p>
						<div v-else class="panel-chart">
							<DoughnutChart
								:labels="positivity.labels"
								:values="positivity.values"
								@select="
									(i: number) =>
										drillBucket(
											'trap_instance',
											'positivity',
											positivity.buckets,
											i,
										)
								"
							/>
						</div>
					</ReportPanel>
				</QuestionPanel>

				<QuestionPanel v-if="selectedQuestion === 'C14'" v-bind="ask('C14')">
					<ReportPanel v-if="report" title="Production share by site">
						<template #help>
							<p>
								Top 15 Sites by site-visits. Ranked tally, not a drill: the
								ledger filters no identifier.
							</p>
						</template>
						<p v-if="siteShares.rows.length === 0" class="panel-empty">
							No site-visits under the current filter.
						</p>
						<div v-else class="panel-chart">
							<BarChart
								horizontal
								:labels="siteShares.labels"
								:datasets="siteShares.datasets"
							/>
						</div>
					</ReportPanel>

					<ReportPanel v-if="report" title="Production share by technician">
						<template #help>
							<p>
								One bar per technician who recorded observations. Click a bar to
								narrow the observations ledger to that person.
							</p>
							<p>
								The bucket key is the person's user id, which the ledger's
								technician filter selects by identity — never by name.
							</p>
						</template>
						<p v-if="applicatorShares.rows.length === 0" class="panel-empty">
							No observations under the current filter.
						</p>
						<div v-else class="panel-chart">
							<BarChart
								horizontal
								:labels="applicatorShares.labels"
								:datasets="applicatorShares.datasets"
								@select="
									(i: number) =>
										drillBucket(
											'observation',
											'creator',
											applicatorShares.rows,
											i,
										)
								"
							/>
						</div>
					</ReportPanel>
				</QuestionPanel>

				<QuestionPanel v-if="selectedQuestion === 'D15'" v-bind="ask('D15')">
					<ReportPanel title="Top products">
						<template #help>
							<p>
								Kept treatments by product. Click a bar to narrow the treatments
								ledger.
							</p>
						</template>
						<p v-if="products.buckets.length === 0" class="panel-empty">
							No kept treatments under the current filter.
						</p>
						<div v-else class="panel-chart">
							<BarChart
								horizontal
								:labels="products.labels"
								:datasets="products.datasets"
								:area-unit="areaUnit"
								@select="
									(i: number) =>
										drillBucket('treatment', 'product', products.buckets, i)
								"
							/>
						</div>
					</ReportPanel>

					<ReportPanel title="Who spends the effort">
						<template #help>
							<p>
								Kept treatments by applicator. Click a bar to narrow the
								treatments ledger to that person.
							</p>
						</template>
						<p v-if="applicators.buckets.length === 0" class="panel-empty">
							No kept treatments under the current filter.
						</p>
						<div v-else class="panel-chart">
							<BarChart
								horizontal
								:labels="applicators.labels"
								:datasets="applicators.datasets"
								:area-unit="areaUnit"
								@select="
									(i: number) =>
										drillBucket('treatment', 'creator', applicators.buckets, i)
								"
							/>
						</div>
					</ReportPanel>

					<ReportPanel v-if="report" title="Spend by product">
						<template #help>
							<p>
								Top 15 products by treatments: what each cost in its own unit,
								and the extent it covered. Quantities are listed per unit and
								never added together.
							</p>
						</template>
						<p v-if="spendProducts.length === 0" class="panel-empty">
							No kept treatments under the current filter.
						</p>
						<div v-else class="table-scroll">
							<table class="report-table">
								<thead>
									<tr>
										<th scope="col">Product</th>
										<th scope="col">Treatments</th>
										<th scope="col">Area ({{ areaUnit }})</th>
										<th scope="col">Applied quantity</th>
									</tr>
								</thead>
								<tbody>
									<tr v-for="row in spendProducts" :key="row.key">
										<th scope="row">{{ row.label }}</th>
										<td>{{ formatCount(row.treatments) }}</td>
										<td>
											{{ row.area == null ? "—" : formatAmount(row.area) }}
										</td>
										<td>{{ unitTotals(row.quantities) || "—" }}</td>
									</tr>
								</tbody>
							</table>
						</div>
					</ReportPanel>

					<ReportPanel v-if="report" title="Spend by site">
						<template #help>
							<p>
								Top 15 Sites by treatments. Ranked tally, not a drill: the
								ledger filters no identifier.
							</p>
						</template>
						<p v-if="spendSites.length === 0" class="panel-empty">
							No kept treatments under the current filter.
						</p>
						<div v-else class="table-scroll">
							<table class="report-table">
								<thead>
									<tr>
										<th scope="col">Site</th>
										<th scope="col">Treatments</th>
										<th scope="col">Area ({{ areaUnit }})</th>
										<th scope="col">Applied quantity</th>
									</tr>
								</thead>
								<tbody>
									<tr v-for="row in spendSites" :key="row.key">
										<th scope="row">{{ row.label }}</th>
										<td>{{ formatCount(row.treatments) }}</td>
										<td>
											{{ row.area == null ? "—" : formatAmount(row.area) }}
										</td>
										<td>{{ unitTotals(row.quantities) || "—" }}</td>
									</tr>
								</tbody>
							</table>
						</div>
					</ReportPanel>

					<ReportPanel v-if="report" title="Recorded field minutes by month">
						<template #help>
							<p>
								Inspection and treatment time, summed per client-local month. A
								lower bound: an observation with no live inspection carries no
								window, so it contributes nothing.
							</p>
						</template>
						<p v-if="fieldMinuteMonths.months.length === 0" class="panel-empty">
							No recorded visit time under the current filter.
						</p>
						<div v-else class="panel-chart">
							<LineChart
								:labels="fieldMinuteMonths.labels"
								:datasets="fieldMinuteDatasets"
							/>
						</div>
					</ReportPanel>
				</QuestionPanel>

				<QuestionPanel v-if="selectedQuestion === 'D16'" v-bind="ask('D16')">
					<ReportPanel title="Production found per visit">
						<p v-if="marginalSites.rows.length === 0" class="panel-empty">
							No site-visits under the current filter, so no return to rank.
						</p>
						<div v-else class="panel-chart">
							<BarChart
								horizontal
								:labels="marginalSites.labels"
								:datasets="marginalSites.datasets"
							/>
						</div>
					</ReportPanel>
				</QuestionPanel>

				<QuestionPanel v-if="selectedQuestion === 'D17'" v-bind="ask('D17')">
					<ReportPanel v-if="report" title="Effort beside productivity">
						<template #help>
							<p>
								One row per Site. Read the effort columns against the
								productivity columns: a place serviced hard that produces little
								is the over-servicing this question looks for. "Found
								production" counts the visits whose observation check found
								production; "wasted" counts the visits that applied no
								intervention — the head-line definition of waste, so a visit can
								be both.
							</p>
						</template>
						<p v-if="effortSites.length === 0" class="panel-empty">
							No site-visits under the current filter.
						</p>
						<div v-else class="table-scroll">
							<table class="report-table">
								<thead>
									<tr>
										<th scope="col">Site</th>
										<th scope="col">Visits</th>
										<th scope="col">Found production</th>
										<th scope="col">Wasted (no intervention)</th>
										<th scope="col">Treatments</th>
										<th scope="col">Minutes</th>
										<th scope="col">Quantity</th>
										<th scope="col">Features (productive)</th>
										<th scope="col">Found</th>
									</tr>
								</thead>
								<tbody>
									<tr v-for="row in effortSites" :key="row.key">
										<th scope="row">{{ row.label }}</th>
										<td>{{ formatCount(row.visits) }}</td>
										<td>{{ formatCount(row.productive_visits) }}</td>
										<td>{{ formatCount(row.wasted_visits) }}</td>
										<td>{{ formatCount(row.treatments) }}</td>
										<td>{{ formatCount(row.minutes) }}</td>
										<td>{{ unitTotals(row.quantities) || "—" }}</td>
										<td>
											{{ formatCount(row.features) }} ({{
												formatCount(row.productive_features)
											}})
										</td>
										<td>{{ formatCount(row.production_found) }}</td>
									</tr>
								</tbody>
							</table>
						</div>
					</ReportPanel>

					<ReportPanel title="Observations by technician">
						<template #help>
							<p>
								{{ technicians.buckets.length }} of
								{{ data.observations_by_technician?.length ?? 0 }} technicians
								shown, by observations recorded. Click a bar to narrow the
								observations ledger to that technician.
							</p>
						</template>
						<p v-if="technicians.buckets.length === 0" class="panel-empty">
							No observations under the current filter.
						</p>
						<div v-else class="panel-chart">
							<BarChart
								horizontal
								:labels="technicians.labels"
								:datasets="technicians.datasets"
								@select="
									(i: number) =>
										drillBucket(
											'observation',
											'creator',
											technicians.buckets,
											i,
										)
								"
							/>
						</div>
					</ReportPanel>
				</QuestionPanel>

				<QuestionPanel v-if="selectedQuestion === 'D18'" v-bind="ask('D18')">
					<ReportPanel title="Usage over time">
						<template #help>
							<p>
								Treatments per month and the area they covered — two charts on
								one month axis. Click either to narrow the ledger to that month.
							</p>
							<p>Applied quantity: {{ appliedQuantity || "none recorded" }}</p>
							<p v-if="!hasUsageArea">
								No area was recorded on the kept treatments, so the area chart
								is omitted.
							</p>
						</template>
						<p v-if="usage.months.length === 0" class="panel-empty">
							No kept treatments under the current filter.
						</p>
						<div v-else class="split">
							<div class="split-row">
								<p class="chart-caption">Treatments applied per month</p>
								<div class="panel-chart">
									<LineChart
										:labels="usage.labels"
										:datasets="usageCountDatasets"
										@select="
											(i: number) =>
												drillMonth(
													'treatment',
													'start_datetime',
													usage.months,
													i,
												)
										"
									/>
								</div>
							</div>
							<div v-if="hasUsageArea" class="split-row">
								<p class="chart-caption">Area treated ({{ areaUnit }})</p>
								<div class="panel-chart">
									<LineChart
										:labels="usage.labels"
										:datasets="usageAreaDatasets"
										@select="
											(i: number) =>
												drillMonth(
													'treatment',
													'start_datetime',
													usage.months,
													i,
												)
										"
									/>
								</div>
							</div>
						</div>
					</ReportPanel>

					<ReportPanel title="Control methods">
						<template #help>
							<p>
								Chemical, biological or physical — the source-reduction mix.
								Click a slice to narrow the treatments ledger.
							</p>
						</template>
						<p v-if="methods.buckets.length === 0" class="panel-empty">
							No kept treatments under the current filter.
						</p>
						<div v-else class="panel-chart">
							<DoughnutChart
								:labels="methods.labels"
								:values="methods.values"
								@select="
									(i: number) =>
										drillBucket('treatment', 'method', methods.buckets, i)
								"
							/>
						</div>
					</ReportPanel>

					<ReportPanel v-if="report" title="Larvae against adults">
						<template #help>
							<p>
								The catalog category split, the same doughnut A4 and B8 draw.
								Denominator: kept treatments.
							</p>
						</template>
						<p
							v-if="treatmentCategories.buckets.length === 0"
							class="panel-empty"
						>
							No kept treatments under the current filter.
						</p>
						<div v-else class="panel-chart">
							<DoughnutChart
								:labels="treatmentCategories.labels"
								:values="treatmentCategories.values"
							/>
						</div>
					</ReportPanel>

					<ReportPanel title="Applied-rate verdicts">
						<template #help>
							<p>
								Whether the chemical mix is even legal to apply: over the label,
								under it, or within it. Click a slice to narrow the treatments
								ledger.
							</p>
						</template>
						<div class="panel-kpi">
							<StatCard
								label="Within label"
								:value="inRangeLabel"
								sub="of treatments with a rate basis"
								:accent="VERDICT_COLORS.in_range"
							/>
						</div>
						<p v-if="verdicts.buckets.length === 0" class="panel-empty">
							No kept treatments carry a rate basis.
						</p>
						<div v-else class="panel-chart">
							<DoughnutChart
								:labels="verdicts.labels"
								:values="verdicts.values"
								:colors="verdictColors"
								@select="
									(i: number) =>
										drillBucket(
											'treatment',
											'applied_rate',
											verdicts.buckets,
											i,
										)
								"
							/>
						</div>
					</ReportPanel>
				</QuestionPanel>

				<QuestionPanel v-if="selectedQuestion === 'D19'" v-bind="ask('D19')">
					<ReportPanel title="Under-serviced sites">
						<p v-if="underServiced.length === 0" class="panel-empty">
							No Site has production found and nothing under current control —
							or there was no production under the current filter.
						</p>
						<div v-else class="table-scroll">
							<table class="report-table">
								<thead>
									<tr>
										<th scope="col">Site</th>
										<th scope="col">Site-visits</th>
										<th scope="col">Found</th>
										<th scope="col">Features (productive)</th>
										<th scope="col">Treatments</th>
										<th scope="col">Quantity</th>
									</tr>
								</thead>
								<tbody>
									<tr v-for="row in underServiced" :key="row.key">
										<th scope="row">{{ row.label }}</th>
										<td>{{ formatCount(row.visits) }}</td>
										<td>{{ formatCount(row.production_found) }}</td>
										<td>
											{{ formatCount(row.features) }} ({{
												formatCount(row.productive_features)
											}})
										</td>
										<td>{{ formatCount(row.treatments) }}</td>
										<td>{{ unitTotals(row.quantities) || "—" }}</td>
									</tr>
								</tbody>
							</table>
						</div>
					</ReportPanel>
				</QuestionPanel>

				<QuestionPanel v-if="selectedQuestion === 'D20'" v-bind="ask('D20')">
					<ReportPanel title="Production outside current control">
						<template #help>
							<p>
								Click a bar to narrow the Features ledger to that control state
								— the ledger's own derived verdict.
							</p>
							<p>
								The ceiling is inferred from control state, not measured: a
								production at a past-control Feature may be a failed treatment,
								a new cohort, or immigration. The report shows the fact and does
								not attribute the cause.
							</p>
						</template>
						<p v-if="outsideControl.buckets.length === 0" class="panel-empty">
							No production was found under the current filter.
						</p>
						<div v-else class="panel-chart">
							<BarChart
								horizontal
								:labels="outsideControl.labels"
								:datasets="outsideControl.datasets"
								@select="
									(i: number) =>
										drillBucket(
											'feature',
											'control_state',
											outsideControl.buckets,
											i,
										)
								"
							/>
						</div>
					</ReportPanel>
				</QuestionPanel>
			</div>
		</template>

		<!-- Mounted once, as the last child of the pane, and never unmounted while
		     the report is on screen: the cards teleport into its host, so a layer
		     that came and went with a question would drop an enlarged card.
		     It renders at the page level rather than inside the rail: the rail
		     slides with a `transform`, and a transformed ancestor becomes the
		     containing block for `position: fixed` descendants, which would pin
		     this overlay to the rail's box for the quarter second of that
		     animation instead of to the window. -->
		<Teleport to="body">
			<ExpandedCardLayer />
		</Teleport>
	</div>
</template>

<style scoped>
/* The report, as a pane of the left-hand tool rail: the chooser of questions
   above, the chosen question's panels below, all of it scrolling in the rail's
   body. It used to be a full-viewport overlay that took the map away to answer
   one question; a rail keeps the map beside the answer. Toasts are Bootstrap's
   fixed layer (z-index 1090), so they still surface above it. */
.analysis-pane {
	display: flex;
	flex-direction: column;
	height: 100%;
	min-height: 0;
	min-width: 0;
}

/* What the report is scoped to. Every panel is computed over this spec, so it
   has to be legible without opening one: a report whose numbers are a mystery
   is worse than no report. */
.analysis-head {
	display: flex;
	align-items: flex-start;
	gap: 8px;
	flex: 0 0 auto;
	margin: 0 0 6px;
}

.analysis-scope {
	flex: 1 1 auto;
	min-width: 0;
	margin: 0;
	font-size: 0.75rem;
	color: #6c757d;
}

/* The applied-filter indicator: a small chip in the head whenever the spec
   narrows anything, with every clause readable in its tooltip. It is a count
   plus an icon so it costs almost no room; the detail is one hover away. */
.analysis-filter-chip {
	display: inline-flex;
	align-items: center;
	gap: 4px;
	flex: 0 0 auto;
	padding: 1px 7px;
	font-size: 0.68rem;
	line-height: 1.6;
	font-variant-numeric: tabular-nums;
	color: #1864ab;
	background: #e7f5ff;
	border: 1px solid #a5d8ff;
	border-radius: 10px;
	cursor: help;
	white-space: pre-line;
}

.analysis-filter-chip .bi {
	font-size: 0.66rem;
}

.analysis-state {
	display: flex;
	flex-direction: column;
	align-items: center;
	justify-content: center;
	flex: 1 1 auto;
}

/* The chooser: one button per question, under the phase it belongs to. It takes
   the height it needs and no more, scrolling within itself once the inventory
   outgrows what the pane can spare, so the chosen question's panels stay in
   view beneath it. */
.analysis-chooser {
	flex: 0 1 auto;
	max-height: 45%;
	overflow-y: auto;
	padding-bottom: 6px;
	border-bottom: 1px solid #dee2e6;
}

.analysis-phase {
	margin: 4px 0 2px;
	font-size: 0.7rem;
	font-weight: 600;
	text-transform: uppercase;
	letter-spacing: 0.03em;
	color: #6c757d;
}

.analysis-choice {
	display: block;
	width: 100%;
	border: 0;
	background: transparent;
	padding: 3px 6px;
	text-align: left;
	font-size: 0.75rem;
	line-height: 1.35;
	color: #212529;
	border-radius: 4px;
	cursor: pointer;
}

.analysis-choice:hover {
	background: #f1f3f5;
}

.analysis-choice.active {
	background: #e7f5ff;
	color: #1864ab;
	font-weight: 600;
}

.analysis-choice-id {
	margin-right: 4px;
	color: #6c757d;
	font-variant-numeric: tabular-nums;
}

/* The chosen question's panels. It scrolls down and never sideways: every
   panel is built to the rail's width, and this is the backstop that keeps one
   wide table from making the whole pane a horizontal scroller. */
.analysis-body {
	flex: 1 1 auto;
	min-width: 0;
	min-height: 0;
	overflow-y: auto;
	overflow-x: hidden;
	padding-top: 10px;
}

/* While a drill-down refetches, the questions keep their place but take no
   clicks: a second click against stale numbers would narrow to the wrong set. */
.analysis-body.is-loading {
	opacity: 0.55;
	pointer-events: none;
}

/* The population question reads its own payload, so its loading state needs a
   height of its own rather than inheriting the report's flex fill. */
.population-state {
	min-height: 160px;
}

/* An older server's payload: the report is simply absent, and the reader is told
   so rather than shown a page of empty panels. */
.report-notice {
	margin: 0 0 12px;
	padding: 6px 10px;
	font-size: 0.75rem;
	color: #663c00;
	background: #fff3cd;
	border: 1px solid #ffe69c;
	border-radius: 4px;
}

/* The clauses this question ignores: a quieter notice than a failure, because
   the reading is complete — it simply has no such dimension. */
.waste-ignored {
	color: #495057;
	background: #f1f3f5;
	border-color: #dee2e6;
}

/* The per-unit quantity list: wraps rather than clips, because a cost proxy that
   hides its own figures is not one. */
.headline-quantity {
	margin: 8px 0 0;
	font-size: 0.7rem;
	line-height: 1.4;
	color: #495057;
	overflow-wrap: anywhere;
}

.kpi-row {
	display: grid;
	grid-template-columns: repeat(auto-fit, minmax(150px, 1fr));
	gap: 8px;
	margin-bottom: 12px;
}

/* Inside a panel the cards sit on the panel's own background, so no extra gap
   belongs under them. The panel element is a child component, so this reaches
   its root. */
.report-panel .kpi-row {
	margin-bottom: 0;
}

.species-grid {
	display: grid;
	grid-template-columns: repeat(auto-fill, minmax(240px, 1fr));
	gap: 10px;
}

.species-cell {
	display: flex;
	flex-direction: column;
	min-height: 190px;
}

.species-cell > .panel-chart {
	min-height: 150px;
}

.panel-kpi {
	margin-bottom: 6px;
}

.panel-chart {
	position: relative;
	flex: 1 1 auto;
	min-height: 150px;
}

/* Small multiples: two charts stacked on one shared month axis. Each row owns
   half the panel's height, and the chart fills it. */
.split {
	display: flex;
	flex-direction: column;
	flex: 1 1 auto;
	gap: 8px;
	min-height: 0;
}

.split-row {
	display: flex;
	flex-direction: column;
	flex: 1 1 0;
	min-height: 150px;
}

.split-row > .panel-chart {
	min-height: 0;
}

.chart-caption {
	margin: 0 0 2px;
	font-size: 0.72rem;
	font-weight: 600;
	color: #495057;
}

.panel-empty {
	display: flex;
	align-items: center;
	justify-content: center;
	flex: 1 1 auto;
	min-height: 150px;
	margin: 0;
	padding: 0 8px;
	text-align: center;
	font-size: 0.75rem;
	color: #adb5bd;
}

/* The cost proxy needs the numbers side by side to be read at all — a litre and
   a pound are two facts, and the effort columns are meaningless without the
   productivity columns beside them — so those panels are tables, not charts.
   The table divides the rail by column rule rather than by its widest cell, so
   it can never be wider than the pane it sits in. */
.table-scroll {
	max-width: 100%;
	overflow-x: hidden;
}

.report-table {
	width: 100%;
	table-layout: fixed;
	border-collapse: collapse;
	font-size: 0.7rem;
	color: #212529;
}

.report-table th,
.report-table td {
	padding: 3px 6px;
	text-align: right;
	white-space: normal;
	overflow-wrap: anywhere;
	font-variant-numeric: tabular-nums;
}

.report-table thead th {
	font-weight: 600;
	color: #6c757d;
	border-bottom: 1px solid #dee2e6;
}

.report-table tbody tr + tr th,
.report-table tbody tr + tr td {
	border-top: 1px solid #f1f3f5;
}

.report-table th[scope="row"] {
	text-align: left;
	font-weight: 500;
	white-space: normal;
}
</style>
